import { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { checkIsTopicCardCountMax, getTopicLibraryCardIcon } from '../../utilities/helpers';
import NarrativesSearchBar from '../NarrativesSearchBar';

import {
  Badge,
  ButtonV2,
  CloseIcon,
  IconButtonV2,
  LoaderSkeleton,
  PlusPlainIcon,
  TopicLibraryCard,
  truncateTextWithEllipsis,
} from '@utilities';

import styles from './_index.module.scss';

const NarrativesTopicsWindow = ({
  activeTopicIds = [],
  availableTags = [],
  hasTagLoadError = false,
  narrativeTopicGroups = null,
  onCloseToolWindow,
  onInsertTopicCard,
  selectedTags = [],
  setSelectedTags = () => {},
}) => {
  const [searchValue, setSearchValue] = useState('');

  const isInitialLoading = !Array.isArray(narrativeTopicGroups);

  const activeTopicIdSet = useMemo(
    () => new Set((activeTopicIds || []).filter((id) => id != null).map(String)),
    [activeTopicIds]
  );
  const handleInsertTopic = (group) => {
    if (
      !group ||
      typeof onInsertTopicCard !== 'function' ||
      checkIsTopicCardCountMax(activeTopicIdSet.size)
    )
      return;
    onInsertTopicCard(group);
  };

  const normalizedSearchValue = searchValue.trim().toLowerCase();

  const filteredNarrativeTopics = useMemo(
    () =>
      (Array.isArray(narrativeTopicGroups) ? narrativeTopicGroups : []).filter((group) =>
        (group.grouping?.toLowerCase() || '').includes(normalizedSearchValue)
      ),
    [narrativeTopicGroups, normalizedSearchValue]
  );

  const favoriteTopics = useMemo(
    () =>
      filteredNarrativeTopics.filter((group) => group.topics?.some((topic) => topic.isFavorited)),
    [filteredNarrativeTopics]
  );

  const otherTopics = useMemo(
    () =>
      filteredNarrativeTopics.filter((group) => !group.topics?.some((topic) => topic.isFavorited)),
    [filteredNarrativeTopics]
  );

  const handleToggleTag = (value) => {
    setSelectedTags((prevSelectedTags) =>
      prevSelectedTags.includes(value)
        ? prevSelectedTags.filter((tag) => tag !== value)
        : [...prevSelectedTags, value]
    );
  };

  const renderLoadingCards = (cardsToRender) =>
    Array.from({ length: cardsToRender }, (_, index) => (
      <TopicLibraryCard key={`loading-card-${index}`}>
        <LoaderSkeleton height={85}>
          <rect height="75" rx="7" ry="7" width="80px" x="0" y="0" />
          <rect height="10" rx="7" ry="15" width="65%" x="110" y="5" />
          <rect height="10" rx="7" ry="15" width="65%" x="110" y="25" />
        </LoaderSkeleton>
      </TopicLibraryCard>
    ));

  const renderTopicCards = (groups) =>
    groups
      .filter(
        (group) =>
          Boolean(group?.grouping) && Array.isArray(group?.topics) && group.topics.length > 0
      )
      .map((group) => {
        const topicIds = group.topics
          .map((topic) => topic?.id)
          .filter((id) => id != null)
          .map(String);
        const addedVariants = topicIds.filter((id) => activeTopicIdSet.has(id)).length;
        const totalVariants = group.topics.length;
        const isDisabled = addedVariants >= totalVariants;
        const isActive = addedVariants > 0;
        const representativeTopic = group.topics[0];

        return (
          <TopicLibraryCard
            className={
              isDisabled
                ? styles['narratives-topics-window-topic-library-card-disabled']
                : styles['narratives-topics-window-topic-library-card']
            }
            footer={
              <div className={styles['topic-card-footer']}>
                <ButtonV2
                  className={styles['narratives-topics-window-insert-button']}
                  disabled={isDisabled}
                  icon={isDisabled ? null : <PlusPlainIcon />}
                  onClick={(event) => {
                    event.stopPropagation();
                    handleInsertTopic(group);
                  }}
                  size="small"
                  variant="subtle"
                >
                  {isDisabled ? 'All variants added' : 'Insert'}
                </ButtonV2>
                {totalVariants > 1 && (
                  <Badge
                    label={
                      addedVariants === 0
                        ? `${totalVariants} Variants`
                        : `${addedVariants}/${totalVariants} variants added`
                    }
                  />
                )}
              </div>
            }
            icon={getTopicLibraryCardIcon(representativeTopic.vizType)}
            isActive={isActive}
            isFooterAlwaysVisible
            key={group.grouping}
            title={
              truncateTextWithEllipsis({ length: 80, text: group.grouping }) || 'Untitled Topic'
            }
          />
        );
      });

  return (
    <div className={styles['topics-window']}>
      <div className={styles['topics-window-header-row']}>
        <strong className={styles['topics-window-header']}>Assemble with Topic Cards</strong>
        {onCloseToolWindow ? (
          <IconButtonV2
            aria-label="Close"
            icon={<CloseIcon />}
            onClick={onCloseToolWindow}
            size="medium"
            tooltip="Close"
            tooltipProps={{ placement: 'left' }}
            variant="default"
          />
        ) : null}
      </div>
      <NarrativesSearchBar
        ariaLabel="Find a topic"
        availableTags={availableTags}
        handleToggleTag={handleToggleTag}
        hasTagLoadError={hasTagLoadError}
        placeholderValue="Find a topic"
        searchValue={searchValue}
        selectedTags={selectedTags}
        setSearchValue={setSearchValue}
      />

      <div className={styles['topics-window-topic-library']}>
        {isInitialLoading ? (
          <>
            {renderLoadingCards(3)}
            {renderLoadingCards(6)}
          </>
        ) : (
          <>
            {favoriteTopics.length > 0 && (
              <>
                <h4 className={styles['topics-window-section-title']}>Favorite Topic Cards</h4>
                {renderTopicCards(favoriteTopics)}
              </>
            )}

            <h4 className={styles['topics-window-section-title']}>
              {favoriteTopics.length > 0 ? 'Other Topic Cards' : 'Topic Cards'}
            </h4>
            {renderTopicCards(otherTopics)}
          </>
        )}
        {!isInitialLoading && filteredNarrativeTopics.length === 0 && (
          <div className={styles['topics-window-no-results']}>No topic cards found.</div>
        )}
      </div>
    </div>
  );
};

NarrativesTopicsWindow.propTypes = {
  activeTopicIds: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.number, PropTypes.string])),
  availableTags: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.string,
    })
  ),
  hasTagLoadError: PropTypes.bool,
  narrativeTopicGroups: PropTypes.oneOfType([
    PropTypes.oneOf([null]),
    PropTypes.arrayOf(
      PropTypes.shape({
        grouping: PropTypes.string,
        tags: PropTypes.arrayOf(PropTypes.string),
        topics: PropTypes.arrayOf(
          PropTypes.shape({
            id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
            isFavorited: PropTypes.bool,
            name: PropTypes.string,
            tags: PropTypes.arrayOf(PropTypes.string),
            variant: PropTypes.object,
            vizType: PropTypes.string,
          })
        ),
      })
    ),
  ]),
  onCloseToolWindow: PropTypes.func,
  onInsertTopicCard: PropTypes.func,
  selectedTags: PropTypes.arrayOf(PropTypes.string),
  setSelectedTags: PropTypes.func,
};

export default NarrativesTopicsWindow;
